import PropTypes from 'prop-types';

import { ContainerProps as MuiContainerProps } from '@mui/material/Container';
import { styled } from '@mui/material/styles';
import { EmailSignup } from '@classity-edtech/ui-auth';
import { useLocation, useNavigate } from 'react-router-dom';
import { webPaths } from '../../routes/paths';
import { Roles } from '@classity-edtech/platform-utils';
import GoogleButton from '../../components/GoogleButton';
import Helmet from '../../components/Helmet';
import { useState } from 'react';
import { useGetPageContentQuery } from '../../services/seoContent';
import { LegalComponent, Sheet } from '@classity-edtech/ui-base/dist/components/Displays';
import { Box } from '@mui/material';
import { CircularProgress, windowExists } from '@classity-edtech/ui-base';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';

export type PolicySection = {
  title: string;
  content: {
    subtitle: string;
    content: string[];
    items: string[];
  }[];
};

export interface SignupProps extends MuiContainerProps {}

export const Signup = ({ className }: SignupProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [openSheet, setOpenSheet] = useState(false);
  const [legalType, setLegalType] = useState<'terms' | 'privacy' | ''>('');

  const { data: privacyPolicy, isLoading: fetchingPrivacyPolicy } = useGetPageContentQuery({
    tag: 'privacyPolicyPage',
    contentType: 'page',
  });

  const { data: termsPolicy, isLoading: fetchingTermsPolicy } = useGetPageContentQuery({
    tag: 'termsOfUsePage',
    contentType: 'page',
  });

  const data =
    legalType === 'privacy' ? privacyPolicy : legalType === 'terms' ? termsPolicy : undefined;

  const handleCloseSheet = () => {
    setOpenSheet(false);
    setLegalType('');
  };

  const handleOpenSheet = (type: 'privacy' | 'terms') => {
    setOpenSheet(true);
    setLegalType(type);
  };

  const extractPolicyHeader = () => {
    const content = data?.sections?.[0];

    return {
      title: content?.fields?.title ?? '',
      effectiveDate: content?.fields?.description?.content?.[0]?.content?.[0]?.value ?? '',
    };
  };

  const extractTextBlocks = description => {
    if (!description?.content) return [];

    return description.content.flatMap(block =>
      (block.content || [])
        .map(item => item?.value)
        .filter(v => typeof v === 'string' && v.trim() !== ''),
    );
  };

  const extractSections = (): PolicySection[] => {
    const sections = data?.sections?.slice(1, -1);

    const sectionMap = {};

    sections?.forEach(section => {
      const sectionTitle = section.fields?.title;
      if (!sectionTitle) return;

      if (!sectionMap[sectionTitle]) {
        sectionMap[sectionTitle] = {
          title: sectionTitle,
          content: [],
        };
      }

      (section.fields?.list || []).forEach(listItem => {
        const fields = listItem?.fields;
        const texts = extractTextBlocks(fields?.description);

        const content = [];
        const items = [];

        texts?.forEach(text => {
          if (text.endsWith(':') || texts?.length === 1) {
            content.push(text);
          } else {
            items.push(text);
          }
        });

        sectionMap[sectionTitle].content.push({
          subtitle: fields?.title || null,
          content,
          items,
        });
      });
    });

    return Object.values(sectionMap);
  };

  const extractContactInformation = () => {
    const content = data?.sections?.[data?.sections?.length - 1];

    return {
      title: content?.fields?.title ?? '',
      description: content?.fields?.description?.content?.[0]?.content?.[0]?.value ?? '',
      contacts:
        content?.fields?.list?.[0]?.fields?.description?.content?.map(
          (block: any) => block?.content?.[0]?.value ?? '',
        ) ?? [],
    };
  };

  const handleNavigation = (path: string) => {
    if (path === webPaths.root) {
      navigate(`/`);
    }

    navigate(`/${path}`);
  };

  const getRole = () => Roles.STUDENT;

  return (
    <>
      <Helmet />
      <EmailSignup
        navigate={handleNavigation}
        roleId={getRole()}
        googleBtn={<GoogleButton text='signup_with' />}
        termsAction={() => handleOpenSheet('terms')}
        privacyAction={() => handleOpenSheet('privacy')}
      />

      {openSheet && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 5,
            backgroundColor: globalColours['$global-colour-background-dimmer'],
          }}
          onClick={handleCloseSheet}
        ></Box>
      )}

      <Sheet
        closeSheet={handleCloseSheet}
        openSheet={openSheet}
        body={
          fetchingPrivacyPolicy || fetchingTermsPolicy ? (
            <Box>
              <CircularProgress />
            </Box>
          ) : (
            <LegalComponent
              header={extractPolicyHeader()}
              sections={extractSections()}
              contacts={extractContactInformation()}
            />
          )
        }
      />
    </>
  );
};

Signup.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

Signup.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(Signup)(
  ({ theme }) => `
`,
);
